import type { ContestWizardState } from './types';

/** Typical campaign order used when scheduling phases back-to-back. */
export const CANONICAL_PHASE_ORDER = [
    'preparation',
    'ideation',
    'pitch',
    'submission',
    'production',
    'judging',
    'vote',
    'event',
    'closed',
] as const;

export type ContestPhaseKey = (typeof CANONICAL_PHASE_ORDER)[number];

export const PHASE_LABELS: Record<string, string> = {
    preparation: 'Preparation',
    ideation: 'Ideation',
    pitch: 'Pitch',
    submission: 'Submission',
    production: 'Production',
    judging: 'Judging',
    vote: 'Vote',
    event: 'Event',
    closed: 'Closed',
};

export function formatPhaseLabel(phase: string): string {
    return PHASE_LABELS[phase] ?? phase.charAt(0).toUpperCase() + phase.slice(1);
}

export function phaseOptionsForCampaign(submissionState: ContestWizardState['submission_state']): string[] {
    const hideVote = submissionState !== 'public_submission';
    return CANONICAL_PHASE_ORDER.filter(p => !(hideVote && p === 'vote'));
}

export function sortPhasesByCanonicalOrder(phases: string[]): string[] {
    const set = new Set(phases);
    return CANONICAL_PHASE_ORDER.filter(p => set.has(p));
}

export function parseSelectedPhases(raw: string | undefined): string[] {
    if (!raw?.trim()) return [];
    return sortPhasesByCanonicalOrder(
        raw
            .split(',')
            .map(s => s.trim())
            .filter(Boolean),
    );
}

export function serializeSelectedPhases(phases: string[]): string {
    return sortPhasesByCanonicalOrder(phases).join(',');
}

export function toggleSelectedPhase(raw: string | undefined, phase: string): string {
    const current = parseSelectedPhases(raw);
    const next = current.includes(phase)
        ? current.filter(p => p !== phase)
        : sortPhasesByCanonicalOrder([...current, phase]);
    return serializeSelectedPhases(next);
}

export const DEFAULT_AI_PHASE_PRESETS = ['submission', 'judging', 'vote'] as const;

export function defaultAiSelectedPhases(submissionState: ContestWizardState['submission_state']): string[] {
    const allowed = new Set(phaseOptionsForCampaign(submissionState));
    return DEFAULT_AI_PHASE_PRESETS.filter(p => allowed.has(p));
}
